import React, { useState } from 'react';
import { Trash2, User, Briefcase, GripVertical, Pencil, CalendarOff, CalendarCheck, Calendar } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, DateAvailabilityMap, ExperienceLevel, ExperienceLevelMap } from '../types';
import { StatusToggle } from './StatusToggle';
interface AvailabilityGridProps {
  contributors: Contributor[];
  solutions: Solution[];
  availability: AvailabilityMap;
  dateAvailability?: DateAvailabilityMap;
  onToggleAvailability: (contributorId: string, solutionId: string) => void;
  onDeleteContributor: (id: string) => void;
  onDeleteSolution: (id: string) => void;
  onUpdateContributorRegion: (id: string, region: Region) => void;
  onContributorClick: (contributorId: string) => void;
  onSolutionClick: (solutionId: string) => void;
  selectedContributorId: string | null;
  selectedSolutionId: string | null;
  selectedStatusFilter: AvailabilityStatus | null;
  onReorderContributors: (fromIndex: number, toIndex: number) => void;
  onReorderSolutions: (fromIndex: number, toIndex: number) => void;
  isAdminAuthenticated: boolean;
  canAdministerEntities: boolean;
  canDeleteEntities: boolean;
  allContributors: Contributor[];
  allSolutions: Solution[];
  onEditContributor: (contributor: Contributor) => void;
  onEditSolution: (solution: Solution) => void;
  onToggleOutOfOffice: (contributorId: string) => void;
  onOpenSchedule: (contributorId: string, solutionId: string) => void;
  experienceLevels?: ExperienceLevelMap;
  onCycleExperienceLevel?: (contributorId: string, solutionId: string) => void;
  selectedExperienceFilter?: ExperienceLevel | null;
  onOpenWeekView?: (contributorId: string) => void;
}
const LEVEL_STYLES: Record<ExperienceLevel, string> = {
  L1: 'bg-slate-100 text-slate-600 border-slate-200',
  L2: 'bg-blue-50 text-blue-600 border-blue-200',
  L3: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  L4: 'bg-violet-50 text-violet-700 border-violet-200'
};
const REGION_COLORS: Record<Region, string> = {
  USA: 'bg-blue-50 text-blue-700 border-blue-200',
  LATAM: 'bg-amber-50 text-amber-700 border-amber-200',
  India: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  EMEA: 'bg-purple-50 text-purple-700 border-purple-200'
};
export function AvailabilityGrid({
  contributors,
  solutions,
  availability,
  dateAvailability = {},
  onToggleAvailability,
  onDeleteContributor,
  onDeleteSolution,
  onUpdateContributorRegion,
  onContributorClick,
  onSolutionClick,
  selectedContributorId,
  selectedSolutionId,
  selectedStatusFilter,
  onReorderContributors,
  onReorderSolutions,
  isAdminAuthenticated,
  canAdministerEntities,
  canDeleteEntities,
  allContributors,
  allSolutions,
  onEditContributor,
  onEditSolution,
  onToggleOutOfOffice,
  onOpenSchedule,
  experienceLevels = {},
  onCycleExperienceLevel,
  selectedExperienceFilter,
  onOpenWeekView
}: AvailabilityGridProps) {
  // Drag state
  const [draggedContributorIndex, setDraggedContributorIndex] = useState<number | null>(null);
  const [dragOverContributorIndex, setDragOverContributorIndex] = useState<number | null>(null);
  const [draggedSolutionIndex, setDraggedSolutionIndex] = useState<number | null>(null);
  const [dragOverSolutionIndex, setDragOverSolutionIndex] = useState<number | null>(null);
  // Contributor drag handlers
  const handleContributorDragStart = (e: React.DragEvent, index: number) => {
    const allIndex = allContributors.findIndex(c => c.id === contributors[index].id);
    setDraggedContributorIndex(allIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `contributor-${allIndex}`);
  };
  const handleContributorDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const allIndex = allContributors.findIndex(c => c.id === contributors[index].id);
    if (draggedContributorIndex !== null && draggedContributorIndex !== allIndex) {
      setDragOverContributorIndex(allIndex);
    }
  };
  const handleContributorDragLeave = () => {
    setDragOverContributorIndex(null);
  };
  const handleContributorDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const allIndex = allContributors.findIndex(c => c.id === contributors[index].id);
    if (draggedContributorIndex !== null && draggedContributorIndex !== allIndex) {
      onReorderContributors(draggedContributorIndex, allIndex);
    }
    setDraggedContributorIndex(null);
    setDragOverContributorIndex(null);
  };
  const handleContributorDragEnd = () => {
    setDraggedContributorIndex(null);
    setDragOverContributorIndex(null);
  };
  // Solution drag handlers
  const handleSolutionDragStart = (e: React.DragEvent, index: number) => {
    const allIndex = allSolutions.findIndex(s => s.id === solutions[index].id);
    setDraggedSolutionIndex(allIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `solution-${allIndex}`);
  };
  const handleSolutionDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const allIndex = allSolutions.findIndex(s => s.id === solutions[index].id);
    if (draggedSolutionIndex !== null && draggedSolutionIndex !== allIndex) {
      setDragOverSolutionIndex(allIndex);
    }
  };
  const handleSolutionDragLeave = () => {
    setDragOverSolutionIndex(null);
  };
  const handleSolutionDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const allIndex = allSolutions.findIndex(s => s.id === solutions[index].id);
    if (draggedSolutionIndex !== null && draggedSolutionIndex !== allIndex) {
      onReorderSolutions(draggedSolutionIndex, allIndex);
    }
    setDraggedSolutionIndex(null);
    setDragOverSolutionIndex(null);
  };
  const handleSolutionDragEnd = () => {
    setDraggedSolutionIndex(null);
    setDragOverSolutionIndex(null);
  };
  if (contributors.length === 0 && solutions.length === 0) {
    return <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-id="element-1" data-mp="2swb6rdi370jk">
        <p className="text-lg font-medium" data-id="element-2" data-mp="2xu4foeary8nj">No data to display</p>
        <p className="text-sm" data-id="element-3" data-mp="32nph6gbktkrh">
          Add contributors and solutions to get started.
        </p>
      </div>;
  }
  if (contributors.length === 0 || solutions.length === 0) {
    return <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300" data-id="element-4" data-mp="3bmko5umg2yvu">
        <p className="text-lg font-medium" data-id="element-5" data-mp="3gkuki8jwo5j1">No matching results</p>
        <p className="text-sm" data-id="element-6" data-mp="3ll9typ12gsvq">Try adjusting your filters to see more data.</p>
      </div>;
  }
  return <div className="relative w-full overflow-hidden border border-slate-200 rounded-lg shadow-sm bg-white flex flex-col h-full" data-id="element-7" data-mp="1maigzmd9vkcv">
      <div className="overflow-auto flex-1" data-id="element-8" data-mp="8cmi7vbhhuna">
        <table className="w-full border-collapse text-xs" data-id="element-9" data-mp="8do3stq4lzj9">
          <thead className="bg-slate-50 text-slate-700 font-semibold sticky top-0 z-20" data-id="element-10" data-mp="3lxp6bk5opm1">
            <tr data-id="element-11" data-mp="3vpq89dywe1xf">
              <th className="sticky left-0 z-30 bg-slate-50 px-3 py-2 border-b border-r border-slate-200 min-w-[180px] w-[180px]" data-id="element-12" data-mp="3vpgqgdo0b12t">
                <div className="flex items-center gap-1.5 text-slate-500" data-id="element-13" data-mp="3qrx2un5f5by7">
                  <Briefcase className="h-3.5 w-3.5" data-id="element-14" data-mp="rc0dqhov3dfw" />
                  <span data-id="element-15" data-mp="r9e8iopg1a0u">Solution</span>
                </div>
              </th>
              {contributors.map((contributor, index) => {
              const isSelected = selectedContributorId === contributor.id;
              const allIndex = allContributors.findIndex(c => c.id === contributor.id);
              const isDragging = draggedContributorIndex === allIndex;
              const isDragOver = dragOverContributorIndex === allIndex;
              // Compute OOO from date range
              const today = new Date().toISOString().split('T')[0];
              const isOOO = contributor.isOutOfOffice || (contributor.oooStartDate && contributor.oooEndDate ? contributor.oooStartDate <= today && contributor.oooEndDate >= today : false);
              const hasScheduledOOO = !!(contributor.oooStartDate && contributor.oooEndDate);
              const isFutureOOO = hasScheduledOOO && contributor.oooStartDate! > today;
              return <th key={contributor.id} draggable={canAdministerEntities} onDragStart={e => handleContributorDragStart(e, index)} onDragOver={e => handleContributorDragOver(e, index)} onDragLeave={handleContributorDragLeave} onDrop={e => handleContributorDrop(e, index)} onDragEnd={handleContributorDragEnd} className={`px-2 py-2 border-b border-r border-slate-200 min-w-[90px] max-w-[110px] group relative transition-all ${isSelected ? 'bg-blue-100' : ''} ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''} ${canAdministerEntities ? 'cursor-grab active:cursor-grabbing' : ''} ${isOOO ? 'bg-slate-100' : ''}`} data-id="element-16" data-mp="1azrofouzsvzg" data-mpi={index}>
                    
                    <div className={`flex flex-col items-center gap-1 ${isOOO ? 'opacity-60' : ''}`} data-id="element-17" data-mp="3a6yp054fpbef">
                      
                      {canAdministerEntities && <div className="text-slate-300 hover:text-slate-500 cursor-grab" data-id="element-18" data-mp="3j7wn66s6yop3">
                          <GripVertical className="h-3 w-3" data-id="element-19" data-mp="3f4qg4wx98thk" />
                        </div>}

                      {/* OOO Badge - shows dates when scheduled */}
                      {isOOO && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[8px] font-bold rounded-full border border-orange-200" title={hasScheduledOOO ? `OOO: ${new Date(contributor.oooStartDate!).toLocaleDateString()} – ${new Date(contributor.oooEndDate!).toLocaleDateString()}` : 'Out of Office'} data-id="element-20" data-mp="uer9cqry4qgj">
                        
                          OOO
                        </span>}

                      {/* Future OOO Badge */}
                      {!isOOO && isFutureOOO && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-medium rounded-full border border-blue-200" title={`OOO scheduled: ${new Date(contributor.oooStartDate!).toLocaleDateString()} – ${new Date(contributor.oooEndDate!).toLocaleDateString()}`} data-id="element-21" data-mp="14c9uk1hq37vj">
                        
                          OOO{' '}
                          {new Date(contributor.oooStartDate!).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric'
                    })}
                        </span>}

                      <button onClick={() => onContributorClick(contributor.id)} className={`block text-[10px] font-medium leading-tight text-center max-w-full cursor-pointer hover:text-[#EB1000] transition-colors ${isSelected ? 'text-blue-700' : isOOO ? 'text-slate-500' : 'text-slate-800'}`} style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }} title={`${contributor.name} (${contributor.region})${isOOO ? ' - Out of Office' : isFutureOOO ? ' - OOO Scheduled' : ''} (click to filter)`} data-id="element-22" data-mp="3fzdah5isvojn">
                        
                        {contributor.name}
                        {!isAdminAuthenticated && <span className="text-slate-400 font-normal" data-id="element-23" data-mp="2ho106ef06owx">
                            {' '}
                            ({contributor.region})
                          </span>}
                      </button>

                      {/* Region dropdown - only for admin */}
                      {isAdminAuthenticated && <select value={contributor.region} onChange={e => onUpdateContributorRegion(contributor.id, e.target.value as Region)} className={`text-[9px] px-1.5 py-0.5 rounded border font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#EB1000] ${REGION_COLORS[contributor.region]}`} data-id="element-24" data-mp="1kv5jk2tajlc6">
                        
                          {REGIONS.map(region => <option key={region} value={region} data-id="element-25" data-mp="3homcujc86ucy">
                              {region}
                            </option>)}
                        </select>}

                      {/* Last Updated + Week view icon inline */}
                      <div className="flex items-center justify-center gap-1" data-id="element-26" data-mp="fpm052jpm5h">
                        {contributor.lastUpdated && <span className="text-[8px] text-slate-400 font-normal leading-tight" title={`Last updated: ${new Date(contributor.lastUpdated).toLocaleString()}`} data-id="element-27" data-mp="4egkafiqz9u6">
                          {(() => {
                        const updated = new Date(contributor.lastUpdated);
                        const now = new Date();
                        const diffMs = now.getTime() - updated.getTime();
                        const diffMins = Math.floor(diffMs / 60000);
                        const diffHours = Math.floor(diffMs / 3600000);
                        const diffDays = Math.floor(diffMs / 86400000);
                        if (diffMins < 1) return 'Just now';
                        if (diffMins < 60) return `${diffMins}m ago`;
                        if (diffHours < 24) return `${diffHours}h ago`;
                        if (diffDays < 7) return `${diffDays}d ago`;
                        return updated.toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        });
                      })()}
                        </span>}
                        {onOpenWeekView && <button onClick={e => {
                      e.stopPropagation();
                      onOpenWeekView(contributor.id);
                    }} className="p-0.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded transition-all flex-shrink-0" title="View weekly schedule" data-id="element-28" data-mp="1p06i2hqpxn33">
                          <Calendar className="h-3 w-3" data-id="element-29" data-mp="1tkmc7qyuvt2j" />
                        </button>}
                      </div>

                      {/* OOO Button - opens modal now */}
                      {isAdminAuthenticated && <button onClick={e => {
                    e.stopPropagation();
                    onToggleOutOfOffice(contributor.id);
                  }} className={`p-0.5 rounded transition-all ${isOOO ? 'text-orange-500 bg-orange-50 hover:bg-orange-100' : isFutureOOO ? 'text-blue-500 bg-blue-50 hover:bg-blue-100' : 'text-slate-400 hover:text-orange-500 hover:bg-orange-50 opacity-0 group-hover:opacity-100'}`} title={isOOO ? 'Edit Out of Office dates' : isFutureOOO ? 'Edit scheduled OOO' : 'Set Out of Office'} data-id="element-30" data-mp="22jpskn6n1udc">

                        <CalendarOff className="h-3 w-3" data-id="element-31" data-mp="f3uoxibmagnm" />
                      </button>}

                      {canAdministerEntities && <div className="flex items-center gap-0.5" data-id="element-32" data-mp="o4sn3jzdjtya">
                          <button onClick={e => {
                      e.stopPropagation();
                      onEditContributor(contributor);
                    }} className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-all" title="Edit contributor" data-id="element-33" data-mp="o4ivjryoleqh">

                            <Pencil className="h-2.5 w-2.5" data-id="element-34" data-mp="17qyqfdbi4cun" />
                          </button>
                          {canDeleteEntities && <button onClick={() => onDeleteContributor(contributor.id)} className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all" title="Delete contributor" data-id="element-35" data-mp="1vf3ydhd0ci2m">
                          
                            <Trash2 className="h-2.5 w-2.5" data-id="element-36" data-mp="17ohrhteqp3u7" />
                          </button>}
                        </div>}
                    </div>
                  </th>;
            })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-37" data-mp="12s12yy3mongl">
            {solutions.map((solution, index) => {
            const isSelected = selectedSolutionId === solution.id;
            const allIndex = allSolutions.findIndex(s => s.id === solution.id);
            const isDragging = draggedSolutionIndex === allIndex;
            const isDragOver = dragOverSolutionIndex === allIndex;
            return <tr key={solution.id} draggable={canAdministerEntities} onDragStart={e => handleSolutionDragStart(e, index)} onDragOver={e => handleSolutionDragOver(e, index)} onDragLeave={handleSolutionDragLeave} onDrop={e => handleSolutionDrop(e, index)} onDragEnd={handleSolutionDragEnd} className={`transition-all ${isSelected ? 'bg-purple-50' : 'hover:bg-slate-50/50'} ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'bg-purple-50 border-t-2 border-t-purple-500' : ''}`} data-id="element-38" data-mp="107wc1u0nhf0v" data-mpi={index}>
                  
                  <td className={`sticky left-0 z-10 px-3 py-1.5 border-r border-slate-200 font-medium group ${isSelected ? 'bg-purple-50' : 'bg-white'} ${canAdministerEntities ? 'cursor-grab active:cursor-grabbing' : ''}`} data-id="element-39" data-mp="7ixtivsm8av1">
                    
                    <div className="flex items-center gap-2" data-id="element-40" data-mp="2j6189whyilhk">
                      {canAdministerEntities && <div className="text-slate-300 hover:text-slate-500 cursor-grab flex-shrink-0" data-id="element-41" data-mp="2o6yr8basjjk4">
                          <GripVertical className="h-3 w-3" data-id="element-42" data-mp="2s67k7dy0bdem" />
                        </div>}
                      <button onClick={() => onSolutionClick(solution.id)} className={`truncate text-xs text-left cursor-pointer hover:text-[#EB1000] transition-colors flex-1 ${isSelected ? 'text-purple-700' : 'text-slate-900'}`} title={`${solution.name} (click to filter)`} data-id="element-43" data-mp="1vit5pmbwxqj8">
                        
                        {solution.name}
                      </button>
                      {canAdministerEntities && <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-44" data-mp="1gr0yjxm9visl">
                          <button onClick={e => {
                      e.stopPropagation();
                      onEditSolution(solution);
                    }} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-all" title="Edit solution" data-id="element-45" data-mp="o5uhhqfo06vd">
                          
                            <Pencil className="h-3 w-3" data-id="element-46" data-mp="5ebf9ltgswue" />
                          </button>
                          {canDeleteEntities && <button onClick={() => onDeleteSolution(solution.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all" title="Delete solution" data-id="element-47" data-mp="egkpeaktmo79">
                          
                            <Trash2 className="h-3 w-3" data-id="element-48" data-mp="3mvl3u1r51yci" />
                          </button>}
                        </div>}
                    </div>
                  </td>
                  {contributors.map(contributor => {
                const status = availability[contributor.id]?.[solution.id] || 'unavailable';
                const expLevel = experienceLevels[contributor.id]?.[solution.id] as ExperienceLevel | undefined;
                const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
                const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
                const cellMatches = statusMatch && expMatch;
                const isHighlighted = (selectedStatusFilter || selectedExperienceFilter) && cellMatches;
                const isDimmed = (selectedStatusFilter || selectedExperienceFilter) && !cellMatches;
                // Compute OOO from date range for cell dimming
                const todayStr = new Date().toISOString().split('T')[0];
                const isContributorOOO = contributor.isOutOfOffice || (contributor.oooStartDate && contributor.oooEndDate ? contributor.oooStartDate <= todayStr && contributor.oooEndDate >= todayStr : false);
                const hasFutureSchedule = dateAvailability[contributor.id]?.[solution.id]?.length > 0;
                // Check if there's an active date override for TODAY that makes this unavailable/limited
                const activeSchedule = dateAvailability[contributor.id]?.[solution.id]?.find(s => s.startDate <= todayStr && s.endDate >= todayStr);
                const hasActiveOverride = !!activeSchedule;
                const isScheduledUnavailable = hasActiveOverride && (activeSchedule.status === 'unavailable' || activeSchedule.status === 'limited');
                return <td key={`${contributor.id}-${solution.id}`} className={`px-1 py-1.5 border-r border-slate-100 text-center transition-all group relative ${isHighlighted ? 'bg-slate-100' : ''} ${isDimmed ? 'opacity-30' : ''} ${isContributorOOO ? 'bg-slate-50' : ''} ${isScheduledUnavailable ? 'bg-orange-50/50' : ''}`} data-id="element-49" data-mp="b3a3whdyf1vu">
                        
                        <div className={`flex justify-center items-center gap-1 relative ${isContributorOOO ? 'opacity-40' : ''} ${isScheduledUnavailable ? 'opacity-40' : ''}`} data-id="element-50" data-mp="1vj50uknp6ir2">
                          
                          <StatusToggle status={status} onClick={() => onToggleAvailability(contributor.id, solution.id)} disabled={!isAdminAuthenticated} data-id="element-51" data-mp="2lvs53qwms422" />
                          

                          {/* Experience Level Badge */}
                          <button onClick={e => {
                      e.stopPropagation();
                      onCycleExperienceLevel?.(contributor.id, solution.id);
                    }} disabled={!isAdminAuthenticated} className={`text-[8px] font-bold leading-none rounded border px-1 py-0.5 transition-all ${isAdminAuthenticated ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'} ${expLevel ? LEVEL_STYLES[expLevel] : 'bg-transparent text-slate-300 border-dashed border-slate-200 opacity-0 group-hover:opacity-100'} ${!isAdminAuthenticated && !expLevel ? 'group-hover:opacity-0' : ''}`} title={expLevel ? `Experience: ${expLevel} (click to change)` : 'Set experience level (click)'} data-id="element-52" data-mp="34u4dvbjmrbx4">
                            
                            {expLevel || 'L?'}
                          </button>

                          {/* TODO: "Schedule availability" feature is hidden pending removal.
                              Remove this button, the onOpenSchedule prop, and related
                              hasActiveOverride/hasFutureSchedule/activeSchedule logic
                              when the feature is officially dropped. */}
                          {false && isAdminAuthenticated && <button onClick={e => {
                      e.stopPropagation();
                      onOpenSchedule(contributor.id, solution.id);
                    }} className={`absolute -right-1 -top-1 p-0.5 rounded-full bg-white shadow-sm border transition-all transform ${hasActiveOverride ? 'text-orange-500 border-orange-300 scale-100' : hasFutureSchedule ? 'text-blue-500 border-blue-200 scale-100' : 'border-slate-200 text-slate-400 hover:text-[#EB1000] hover:border-[#EB1000] scale-0 group-hover:scale-100'}`} title={hasActiveOverride ? `Scheduled: ${activeSchedule.status === 'unavailable' ? 'Not Qualified' : activeSchedule.status === 'limited' ? 'Qualified not Available' : 'Qualified and Available'} until ${new Date(activeSchedule.endDate).toLocaleDateString()}` : 'Schedule availability'} data-id="element-53" data-mp="2ehiu1tynxawz">

                            <Calendar className="h-2.5 w-2.5" data-id="element-54" data-mp="1tf8hbmyft3kd" />
                          </button>}
                        </div>
                      </td>;
              })}
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}